import React from "react";
import { createListTableColumns, depascalize, ensure, Grid, Link, ListTable, Stack, Text } from "sprint-common-web";

import { AssetOperationType } from "../../../assets/common/types/AssetOperationType";
import { renderAssetContentDetails } from "../../../assets/common/utils/renderAssetContentDetails";
import { renderAssetOperationType } from "../../../assets/common/utils/renderAssetOperationType";
import { LanguageDetailsInfo } from "../../../common/components/details-info/LanguageDetailsInfo";
import { VideoDetailsInfo } from "../../../common/components/details-info/VideoDetailsInfo";
import { DynamicSubtitleType } from "../../../common/types/DynamicSubtitleType";
import { RecordType } from "../../../common/types/RecordType";
import { renderRecordType } from "../../../common/utils/renderRecordType";
import { AssetOperationListItem } from "./AssetOperationListItem";

export type AssetOperationListItemWithHref = AssetOperationListItem & {
    href?: string;
    resultStatus: string;
};

interface AssetOperationListProps {
    operations: AssetOperationListItemWithHref[];
    isLoading: boolean;
}

export const AssetOperationList = ({ operations, isLoading }: AssetOperationListProps): JSX.Element => {
    return (
        <Stack tokens={{ padding: "0 0 20px 0" }}>
            <ListTable isLoading={isLoading} columns={columns} items={operations} layoutMode="fixedColumns" />
        </Stack>
    );
};

const renderUniquenessKey = (item: AssetOperationListItemWithHref): JSX.Element => {
    ensure(item);

    const linkDisabled = isLinkDisabled(item);

    switch (item.recordType) {
        case RecordType.Video:
            if (linkDisabled) {
                return (
                    <VideoDetailsInfo
                        key="video"
                        subtitle={item.uniquenessKey.subtitleLanguage}
                        aspectRatio={item.uniquenessKey.aspectRatio}
                        aspectType={item.uniquenessKey.aspectType}
                    />
                );
            }
            return (
                <Link href={`#${item.href}`} target="_blank">
                    <VideoDetailsInfo
                        key="video"
                        subtitle={item.uniquenessKey.subtitleLanguage}
                        aspectRatio={item.uniquenessKey.aspectRatio}
                        aspectType={item.uniquenessKey.aspectType}
                    />
                </Link>
            );
        case RecordType.Audio:
            if (linkDisabled) {
                return <LanguageDetailsInfo key="audio" language={item.uniquenessKey.soundtrackLanguage} />;
            }
            return (
                <Link href={`#${item.href}`} target="_blank">
                    <LanguageDetailsInfo key="audio" language={item.uniquenessKey.soundtrackLanguage} />
                </Link>
            );
        case RecordType.Subtitle:
            const subtitleInfo = (
                <Grid>
                    <LanguageDetailsInfo
                        key="audio"
                        language={item.uniquenessKey.subtitleLanguage}
                        subtitleType={depascalize(DynamicSubtitleType[item.uniquenessKey.subtitleType])}
                    />
                </Grid>
            );
            if (linkDisabled) {
                return subtitleInfo;
            }
            return (
                <Grid>
                    <Link href={isLinkDisabled(item) ? undefined : `#${item.href}`} target="_blank">
                        {subtitleInfo}
                    </Link>
                </Grid>
            );
        default:
            return <Text variant="smallPlus">-</Text>;
    }
};

const renderResultStatus = (item: AssetOperationListItemWithHref): JSX.Element => {
    if (!item) return <Stack data-testid="asset-result-status"></Stack>;

    return <Stack data-testid="asset-result-status">{depascalize(item.resultStatus)}</Stack>;
};

const columns = createListTableColumns<AssetOperationListItemWithHref>([
    {
        type: "basic",
        key: "uniquenessKey",
        name: "Details",
        minWidth: 120,
        maxWidth: 320,
        onRender: renderUniquenessKey
    },
    {
        type: "basic",
        key: "content",
        name: "Content",
        minWidth: 120,
        maxWidth: 320,
        onRender: renderAssetContentDetails
    },
    {
        type: "basic",
        key: "recordType",
        name: "Record type",
        minWidth: 120,
        onRender: renderRecordType
    },
    {
        type: "basic",
        key: "operationType",
        name: "Operation type",
        minWidth: 120,
        onRender: renderAssetOperationType
    },
    {
        type: "basic",
        key: "resultStatus",
        name: "Result status",
        minWidth: 120,
        onRender: renderResultStatus
    }
]);

const isLinkDisabled = (item: AssetOperationListItem): boolean => {
    return item.operationType === AssetOperationType.New || item.operationType === AssetOperationType.Ignored;
};
